import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Palette } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Home-page campaign banner for the 2026 Wine of the Year artist contest.
 * Renders just below the hero. Stage-aware: once submissions are open it
 * switches to a "submit your design" call to action.
 */
const HomeWineOfTheYearBanner = () => {
  const [submissionsOpen, setSubmissionsOpen] = useState(false);
  const [votingOpen, setVotingOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/contest/config`)
      .then((r) => {
        setSubmissionsOpen(!!r.data?.submissionsOpen);
        setVotingOpen(r.data?.votingState === 'open');
      })
      .catch(() => {});
  }, []);

  const eyebrow = votingOpen
    ? 'THE LOKAL WINERY · VOTING IS NOW OPEN'
    : submissionsOpen
      ? 'THE LOKAL WINERY · SUBMISSIONS ARE NOW OPEN'
      : 'THE LOKAL WINERY · 2026 WINE OF THE YEAR';
  const headline = votingOpen
    ? 'Help Choose Our 2026 Wine of the Year.'
    : submissionsOpen
      ? 'Your Art Could Be Our 2026 Wine of the Year.'
      : 'Your Art Could Be On Our Next Bottle.';
  const sub = votingOpen
    ? 'The artwork is in — now it\u2019s your turn. Vote for the design you want to see on our 2026 Wine of the Year bottles, made right here in Fenelon Falls.'
    : submissionsOpen
      ? "Submissions are now open. Submit your original artwork for the chance to have your design featured on our 2026 Wine of the Year — bottled right here in Fenelon Falls."
      : "We're searching for a local artist to design our 2026 Wine of the Year. Free to enter. Community voted. Created right here in Fenelon Falls.";
  const ctaLabel = votingOpen ? 'Vote Now' : submissionsOpen ? 'Enter the Contest' : 'Get on the List';
  const badge = votingOpen ? 'VOTING NOW OPEN' : submissionsOpen ? 'SUBMISSIONS NOW OPEN' : null;

  return (
    <section
      data-testid="home-woty-banner"
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(115deg, #722F37 0%, #4A2E1E 100%)',
      }}
    >
      <div
        aria-hidden
        className="absolute -right-10 -top-16 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(253,185,19,0.35), transparent 70%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
        <div className="flex-1">
          {badge && (
            <span
              className="inline-block mb-3 px-3 py-1 rounded-full text-[11px] font-bold tracking-wide"
              style={{ background: '#FDB913', color: '#2a1a10' }}
              data-testid="home-woty-open-badge"
            >
              {badge}
            </span>
          )}
          <p className="text-[11px] tracking-[3px] font-semibold" style={{ color: '#FDB913' }}>
            {eyebrow}
          </p>
          <h2
            className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#F5EBDC] leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {headline}
          </h2>
          <p className="mt-3 text-[#F5EBDC] opacity-90 max-w-xl text-sm sm:text-base">{sub}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Link
            to="/wine-of-the-year"
            data-track="contest:home_woty_banner"
            data-testid="home-woty-cta"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-bold text-gray-900 shadow-lg hover:brightness-110 transition"
            style={{ background: '#FDB913' }}
          >
            <Palette className="w-4 h-4" />
            {ctaLabel}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeWineOfTheYearBanner;
